import { useEffect, useRef } from "react";
import ModalView from "@modals/modalview/ModalView";
import styles from "./SendOtp.module.css";
import { generateOtp } from "@services/authenticationService";
import { setComponentToShow } from "@store/ModalToShow";
import { useDispatch, useSelector } from "react-redux";
import { environment } from "@environments/environment";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

interface SendOtpProps {
  isOpen: boolean;
  onClose: () => void;
}

function SendOtp({ isOpen, onClose }: SendOtpProps) {
  const dispatch = useDispatch();

  const captchaRef = useRef<HTMLDivElement>(null);
  const captchaWidgetIdRef = useRef<number | null>(null);

  const phoneNumber = localStorage.getItem("phoneNumber");
  const countryCode = localStorage.getItem("countryCode");
  const displayNumber = `${countryCode}${phoneNumber}`;
  const isForgotPassword = useSelector(
    (state: any) => state.loginSessionData.isForgotPassword
  );

  const SITE_KEY = environment.captchaSiteKey;
  useEffect(() => {
    if (!isOpen) return;

    const initCaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.render) {
        renderCaptcha();
      } else {
        setTimeout(initCaptcha, 100);
      }
    };

    initCaptcha();

    return () => {
      if (captchaWidgetIdRef.current !== null && window.grecaptcha) {
        try {
          window.grecaptcha.reset(captchaWidgetIdRef.current);
        } catch (error) {
          console.error("Error resetting captcha:", error);
        }
      }
    };
  }, [isOpen]);

  const renderCaptcha = () => {
    if (!captchaRef.current) {
      console.log("Captcha ref not available");
      return;
    }

    if (captchaWidgetIdRef.current !== null) {
      return;
    }

    try {
      if (window.grecaptcha && window.grecaptcha.render) {
        captchaWidgetIdRef.current = window.grecaptcha.render(
          captchaRef.current,
          {
            sitekey: SITE_KEY,
            size: "invisible",
            callback: onCaptchaResolved,
          }
        );
        console.log(
          "Captcha rendered successfully, ID:",
          captchaWidgetIdRef.current
        );
      }
    } catch (error) {
      console.error("Error rendering captcha:", error);
    }
  };

  const onCaptchaResolved = (token: string) => {
    console.log("Token received:", token);
    sendOtpWithToken(token);
  };

  const executeCaptcha = () => {
    if (!window.grecaptcha) {
      console.error("grecaptcha not loaded yet");
      return;
    }

    if (captchaWidgetIdRef.current === null) {
      renderCaptcha();
      setTimeout(() => {
        if (captchaWidgetIdRef.current !== null) {
          console.log("Retrying execute after render");
          window.grecaptcha.execute(captchaWidgetIdRef.current);
        } else {
          console.error("Still couldn't initialize captcha");
        }
      }, 1000);
      return;
    }

    try {
      window.grecaptcha.execute(captchaWidgetIdRef.current);
      console.log("Captcha executed successfully");
    } catch (error) {
      console.error("Error executing captcha:", error);
    }
  };

  const sendOtpWithToken = async (token: string) => {
    const phoneDataFromStorage = localStorage.getItem("phoneData");
    if (!phoneDataFromStorage) return;

    const phoneData = JSON.parse(phoneDataFromStorage);

    const payload = { ...phoneData, captcha_token: token };

    try {
      const res = await generateOtp(payload);
      if (res) {
        if (captchaWidgetIdRef.current !== null && window.grecaptcha) {
          window.grecaptcha.reset(captchaWidgetIdRef.current);
        }
        dispatch(setComponentToShow("VerifyOtp"));
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      if (captchaWidgetIdRef.current !== null && window.grecaptcha) {
        window.grecaptcha.reset(captchaWidgetIdRef.current);
      }
    }
  };

  const handleSendOtpClick = async () => {
    executeCaptcha();
  };

  const handleLoginWithPassword = () => {
    const phoneDataFromStorage = localStorage.getItem("phoneData");
    if (!phoneDataFromStorage) return;
    dispatch(setComponentToShow("LoginWithPassword"));
  };

  return (
    <>
      {isOpen && (
        <div
          ref={captchaRef}
          className="g-recaptcha"
          data-sitekey={SITE_KEY}
          data-callback="onCaptchaResolved"
          data-size="invisible"
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 9999,
          }}
        />
      )}

      <ModalView isOpen={isOpen} onClose={onClose}>
        <div className={styles.otpLoginContainer}>
          <div className={styles.otpHeader}>
            <h2>OTP Login</h2>
            <button className={styles.closeBtn} onClick={onClose}>
              &times;
            </button>
          </div>

          <p className={styles.otpSubText}>
            Please enter your registered phone number to get started
          </p>

          <input
            type="text"
            placeholder={displayNumber}
            className={styles.phoneInput}
            readOnly
          />

          <button className={styles.sendOtpBtn} onClick={handleSendOtpClick}>
            SEND OTP
          </button>

          {!isForgotPassword && (
            <div
              className={styles.loginWithPassword}
              onClick={handleLoginWithPassword}
            >
              LOGIN WITH PASSWORD
            </div>
          )}
        </div>
      </ModalView>
    </>
  );
}

export default SendOtp;
